import { i18n } from "@/setup/i18n-setup";
import Vue from "vue";
import { Arc1255BluetoothNfcReader } from "./arc-1255-bluetooth-nfc-reader";
import { BrowserInfo } from "./browser";
import { ElectronNfcReader } from "./electron/electron-nfc";
import { EventEmitter } from "./event-emitter";

class UnifiedNfcReaderClass extends EventEmitter {
	public readonly READER_SCANNED_EVENT = "nfc-scanned";
	private arc1255IsListening:boolean = false;
	private initialized:boolean = false;
	private wsThreadId:number = 0;
	private ws:WebSocket | null=  null;
	private vue:Vue | null = null;

	public async init(vue:Vue){
		if (this.initialized) return;
		this.vue = vue;
		this.wsThreadId = Math.random();
		this.connectToWs(this.wsThreadId);
		this.initialized = true;
		this.connectToArc1255OnClick = this.connectToArc1255OnClick.bind(this);
		if (BrowserInfo.isElectron()){
			ElectronNfcReader.on(ElectronNfcReader.READER_SCANNED_EVENT, (uid:string)=>{
				this.emit(this.READER_SCANNED_EVENT, uid);
			});
		} else if (this.getAutoConnectToArc1255()) {
			window.addEventListener("click", this.connectToArc1255OnClick);
		}
	}

	private async connectToArc1255OnClick(){
		window.removeEventListener("click", this.connectToArc1255OnClick);
		if (!this.getAutoConnectToArc1255()) return;
		this.connectToArc1255();
	}

	private async connectToArc1255(){
		if (this.arc1255IsListening) return;
		this.arc1255IsListening = true;
		await Arc1255BluetoothNfcReader.connect();
		Arc1255BluetoothNfcReader.on(Arc1255BluetoothNfcReader.READER_SCANNED_EVENT, (uid:string)=>{
			this.emitUidScannedEvent(uid);
		});

	}

	public async onLock(){
		if (this.arc1255IsListening) return;
		if (!this.getAutoConnectToArc1255()) return;
		this.connectToArc1255();
	}

	public setAutoConnectToArc1255(value:boolean){
		if (!Arc1255BluetoothNfcReader.canConnect()) {
			value = false;
		}
		localStorage.setItem("connet-to-arc1255-on-lock", `${value}`);
	}

	public getAutoConnectToArc1255():boolean{
		return localStorage.getItem("connet-to-arc1255-on-lock") == "true";
	}

	private emitUidScannedEvent(uid:string){
		if (!document.hasFocus()) return;
		this.emit(this.READER_SCANNED_EVENT, uid);
	}

	private connectToWs(threadId:number, reconnectAttempt:number = 0){
		if (threadId != this.wsThreadId) return;
		let url = localStorage.getItem("nfc-websocket-url");
		if (this.ws) {
			this.ws.close();
			this.ws = null;
		}
		if (!url) return;
		console.log("Connecting to nfc url", url, threadId);
		let connectionWasEstablished = false;
		this.ws = new WebSocket(url);
		this.ws.addEventListener("message",(message)=>{
			console.log("Got message", message.data);
			let data = JSON.parse(message.data) as { event:string, data:any };
			if (data.event != "nfc-connected") return;
			let tag = data.data as {uid:string};
			this.emitUidScannedEvent(tag.uid);
		});
		this.ws.addEventListener("close", ()=>{
			console.log("NFC WebSocket close", threadId);
			this.ws = null;
			this.connectToWs(threadId, reconnectAttempt+1);
		});
		this.ws.addEventListener("open", ()=>{
			connectionWasEstablished = true;
			console.log("NFC WebSocket opened");
		});
		this.ws.addEventListener("error", (err)=>{
			if (this.vue && (reconnectAttempt == 0 || connectionWasEstablished)) {
				let message = i18n.t("components.LockScreen.cant-connect-to-nfc-bridge-server");
				this.vue.$wf.notify(message.toString(), "danger", 3000, "exclamation-triangle");
			}
			console.error("NFC WebSocket error", err);
		});
	}

	public setWebSocketUrl(url:string){
		url = url.trim();
		localStorage.setItem("nfc-websocket-url", url);
		this.wsThreadId = Math.random();
		this.connectToWs(this.wsThreadId, 0);
	}

	public getWebSocketUrl():string{
		return localStorage.getItem("nfc-websocket-url") || "";
	}
}


export const UnifiedNfcReader = new UnifiedNfcReaderClass();
